/**
 * ContentCard Component - Microsoft 365 Admin Console Style
 * Reusable card component for content sections
 */

import React from 'react';
import { Card, makeStyles, Text } from '@fluentui/react-components';

const useStyles = makeStyles({
  card: {
    marginBottom: '16px',
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  header: {
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#323130',
  },
  subtitle: {
    fontSize: '13px',
    color: '#605e5c',
    marginTop: '4px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  content: {
    color: '#323130',
  },
  footer: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #e1dfdd',
  },
});

interface ContentCardProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  noPadding?: boolean;
}

export const ContentCard: React.FC<ContentCardProps> = ({
  title,
  subtitle,
  actions,
  children,
  footer,
  noPadding = false,
}) => {
  const styles = useStyles();

  return (
    <Card className={styles.card} style={noPadding ? { padding: 0 } : {}}>
      {(title || subtitle || actions) && (
        <div className={styles.header}>
          <div>
            {title && <Text className={styles.title}>{title}</Text>}
            {subtitle && <Text className={styles.subtitle}>{subtitle}</Text>}
          </div>
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
      )}

      <div className={styles.content} style={noPadding ? { padding: '20px' } : {}}>
        {children}
      </div>

      {footer && <div className={styles.footer}>{footer}</div>}
    </Card>
  );
};

/**
 * StatCard Component - For dashboard statistics
 */
const useStatStyles = makeStyles({
  statCard: {
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-2px)',
    },
  },
  iconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '4px',
    backgroundColor: '#008272',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '24px',
  },
  statsContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statLabel: {
    fontSize: '13px',
    color: '#605e5c',
    fontWeight: '400',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  statChange: {
    fontSize: '12px',
    marginTop: '4px',
  },
  statChangePositive: {
    color: '#107c10',
  },
  statChangeNegative: {
    color: '#d13438',
  },
});

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  change?: {
    value: string;
    positive?: boolean;
  };
  onClick?: () => void;
}

export const StatCard: React.FC<StatCardProps> = ({ icon, label, value, change, onClick }) => {
  const styles = useStatStyles();

  return (
    <Card className={styles.statCard} onClick={onClick}>
      <div className={styles.iconContainer}>{icon}</div>
      <div className={styles.statsContent}>
        <Text className={styles.statLabel}>{label}</Text>
        <Text className={styles.statValue}>{value}</Text>
        {change && (
          <Text
            className={`${styles.statChange} ${
              change.positive ? styles.statChangePositive : styles.statChangeNegative
            }`}
          >
            {change.value}
          </Text>
        )}
      </div>
    </Card>
  );
};

/**
 * CardGrid Component - For laying out multiple cards
 */
const useGridStyles = makeStyles({
  grid: {
    display: 'grid',
    gap: '16px',
    marginBottom: '16px',
  },
  grid2: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  },
  grid3: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  },
  grid4: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  },
});

interface CardGridProps {
  children: React.ReactNode;
  columns?: 2 | 3 | 4;
}

export const CardGrid: React.FC<CardGridProps> = ({ children, columns = 3 }) => {
  const styles = useGridStyles();
  const gridClass = columns === 2 ? styles.grid2 : columns === 3 ? styles.grid3 : styles.grid4;

  return <div className={`${styles.grid} ${gridClass}`}>{children}</div>;
};
